import type { ChartSeries, PanelId } from './seriesTypes';

/** The pattern of a series that is dashed wherever it is drawn. */
export const HOME_DASH = '5 4';

/** The pattern of the last segment of a line, up to its streamed price. */
export const LIVE_TAIL_DASH = '4 3';

/** The opacity of a faint line: the old one, while a new one is computed. */
export const FAINT_OPACITY = 0.35;

/**
 * A faint line that is dashed because it is away from home is dim twice: by
 * its gaps and by its opacity. A little more opacity keeps it readable.
 */
export const FAINT_AWAY_DASH_OPACITY = 0.5;

/**
 * The pattern a series has only because it is drawn outside its home panel,
 * or undefined: at home, or when the series has none. A series opts in with
 * `awayDash`, so the lines of other series never change.
 */
export function awayDashOf(
    series: ChartSeries,
    panelId: PanelId
): string | undefined {
    return series.home !== panelId ? series.awayDash : undefined;
}

/**
 * The stroke-dasharray of a line in a panel. Away from home the away pattern
 * replaces the home one, so a run that is dashed at home (the pinned clock) can
 * still be told apart from one that is only dashed away from home.
 */
export function dashOf(
    series: ChartSeries,
    panelId: PanelId
): string | undefined {
    return (
        awayDashOf(series, panelId) ?? (series.dashed ? HOME_DASH : undefined)
    );
}

/** The stroke-opacity of a line in a panel, or undefined when it is not faint. */
export function faintOpacityOf(
    series: ChartSeries,
    panelId: PanelId
): number | undefined {
    if (!series.faint) return undefined;
    return awayDashOf(series, panelId) !== undefined
        ? FAINT_AWAY_DASH_OPACITY
        : FAINT_OPACITY;
}
